import { CheckCircle2, Circle, AlertTriangle, AlertCircle, Info } from "lucide-react";
import { TaskPriority } from "../definition";

interface CalendarEvent {
    id: string;
    title: string;
    time: string;
    colorClass: string;
    isCompleted?: boolean;
    priority?: TaskPriority;
    stepsCount?: number;
    completedStepsCount?: number;
    onDragStart?: (e: React.DragEvent) => void;
    onDragEnd?: (e: React.DragEvent) => void;
    onToggleCompletion?: (e: React.MouseEvent) => void;
}


/**
 * Event Pill component (Individual task block)
 */
const EventPill: React.FC<CalendarEvent & { onClick?: (e: React.MouseEvent) => void }> = ({
    title,
    time,

    isCompleted,
    priority,
    stepsCount,
    completedStepsCount,
    onDragStart,
    onDragEnd,
    onToggleCompletion,
    id,
    onClick
}) => {


    /**
     * Gets styles (bg, accent, text) based on priority level.
     */
    const getPriorityStyles = () => {
        if (isCompleted) return {
            bg: "bg-gray-50/60 opacity-80 backdrop-grayscale",
            accent: "bg-gray-300",
            text: "text-gray-400",
            border: "border-gray-200"
        };

        switch (priority) {
            case 'urgent': return { bg: "bg-red-50/95", accent: "bg-red-500", text: "text-red-900", border: "border-red-100" };
            case 'high': return { bg: "bg-orange-50/95", accent: "bg-orange-500", text: "text-orange-900", border: "border-orange-100" };
            case 'medium': return { bg: "bg-emerald-50/95", accent: "bg-[#3d5a35]", text: "text-[#1a2e15]", border: "border-emerald-100" };
            case 'low': return { bg: "bg-blue-50/95", accent: "bg-blue-400", text: "text-blue-900", border: "border-blue-100" };
            default: return { bg: "bg-gray-50/95", accent: "bg-gray-400", text: "text-gray-900", border: "border-gray-100" };
        }
    };

    const styles = getPriorityStyles();


    return (
        <div
            draggable={!!onDragStart}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onClick={onClick}
            className={`relative mb-2 rounded-xl flex overflow-hidden cursor-pointer hover:shadow-md transition-all shadow-sm group border backdrop-blur-sm ${styles.bg} ${styles.border} cardShadow`}
            style={{ minHeight: '64px' }}
        >
            {/* Left accent vertical bar */}
            <div className={`w-1.5 self-stretch ${styles.accent}`} />

            <div className="flex flex-col flex-1 px-4 py-2.5 gap-1.5 justify-center overflow-hidden">

                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {isCompleted ? (
                            <CheckCircle2
                                size={13}
                                className="text-emerald-500 hover:scale-110 transition-transform"
                                onClick={onToggleCompletion}
                            />
                        ) : (
                            <Circle
                                size={13}
                                className="text-gray-300 group-hover:text-[#3d5a35] hover:scale-110 transition-transform"
                                onClick={onToggleCompletion}
                            />
                        )}

                        <span className="text-[10px] font-black text-gray-500/80 tracking-widest uppercase">{time}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        {/* Status Icon based on priority */}
                        {priority === 'urgent' && <AlertCircle size={14} className="text-red-500" />}
                        {priority === 'high' && <AlertTriangle size={14} className="text-orange-500" />}
                        {priority === 'medium' && <Info size={14} className="text-[#3d5a35]" />}
                        {priority === 'low' && <Info size={14} className="text-blue-400" />}
                    </div>

                </div>

                <div className={`font-bold text-[13px] leading-tight transition-all pr-1 
                    ${isCompleted ? 'text-gray-400 line-through decoration-gray-300' : styles.text}`}>
                    {title}
                </div>


                {stepsCount !== undefined && stepsCount > 0 && (
                    <div className="flex items-center gap-2 mt-0.5">
                        <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                            <div
                                className={`h-full transition-all duration-500 ${isCompleted ? 'bg-gray-300' : 'bg-[#3d5a35]'}`}
                                style={{ width: `${(completedStepsCount || 0) / stepsCount * 100}%` }}
                            />
                        </div>
                        <span className="text-[9px] font-black text-gray-400 shrink-0">
                            {completedStepsCount || 0}/{stepsCount}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};


export default EventPill;